import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { providerSchema } from "~/schemas/SignUp/InsuranceProvider";
import { Dropdown } from "~/shared/components/ui/Dropdown";
import { useAutoAdvanceOnSelect } from "~/shared/hooks/useAutoAdvanceOnSelect";
import { useSubStepTracking } from "~/shared/hooks/useFunnelTracking";
import { INSURANCE_PROVIDERS } from "~/shared/utils/constants";
import { useSignUpStore } from "~/stores";
import { tw } from "~/utils";
import { ActionButtons } from "../../ActionButtons";
import { ErrorMessage } from "../../ErrorMessage";
import { FormContentContainer } from "../../FormContentContainer";

// Typed, so renaming the enum key fails type-check rather than silently unpinning it.
const OTHER_PROVIDER_KEY: keyof typeof INSURANCE_PROVIDERS = "other";

// Sorted here rather than in the enum so newly added providers land in the right
// place on their own. "Other" is a catch-all, not a provider, so it stays last.
const providersOptions: { value: string; label: string }[] = Object.keys(
  INSURANCE_PROVIDERS,
)
  .map((key) => ({
    value: key as keyof typeof INSURANCE_PROVIDERS,
    label: INSURANCE_PROVIDERS[key as keyof typeof INSURANCE_PROVIDERS],
  }))
  .sort((a, b) => {
    if (a.value === OTHER_PROVIDER_KEY) return 1;
    if (b.value === OTHER_PROVIDER_KEY) return -1;
    return a.label.localeCompare(b.label);
  });

interface ProviderFormProps {
  onPrev: () => void;
  onNext: (response?: string) => void;
  isSecondaryProvider?: boolean;
}

export const ProviderForm = ({
  onPrev,
  onNext,
  stepId = "primary_insurance",
  isSecondaryProvider = false,
}: ProviderFormProps & { stepId?: string }) => {
  const { setPatientInsuranceField, resetInsuranceFields, patient } =
    useSignUpStore();
  const { primaryInsurance, secondaryInsurance } = patient;

  const defaultValue = isSecondaryProvider
    ? secondaryInsurance?.providerName
    : primaryInsurance.providerName;
  const autoAdvanceOnSelect = useAutoAdvanceOnSelect(Boolean(defaultValue));

  const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
    parentStep: isSecondaryProvider
      ? "secondary_insurance"
      : "primary_insurance",
    subStep: "provider", // see table below
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof providerSchema>>({
    mode: "onBlur",
    resolver: zodResolver(providerSchema),
    defaultValues: {
      provider: defaultValue ?? "",
    },
  });

  useEffect(() => {
    setValue("provider", defaultValue ?? "");
  }, [defaultValue, setValue]);

  const title = isSecondaryProvider
    ? "Who is your secondary insurance provider?"
    : "Who is your primary insurance provider?";

  const onSubmit: SubmitHandler<z.infer<typeof providerSchema>> = (data) => {
    resetInsuranceFields(data.provider, isSecondaryProvider);
    trackComplete({ plan_type: data.provider });

    onNext(data.provider);
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientInsuranceField("providerName", "", isSecondaryProvider);
    trackBack();
    onPrev();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-20 self-center" />
        <div>
          <h1 className="font-display text-2xl">{title}</h1>
        </div>
        <div className={tw("flex w-full flex-col gap-5", !errors && "mb-8")}>
          <Dropdown
            control={control}
            options={providersOptions}
            {...register("provider")}
            onValueChange={() => {
              // Frontier: picking a provider advances; on revisit, wait for Next.
              if (autoAdvanceOnSelect) {
                void handleSubmit(onSubmit, onError)();
              }
            }}
          />
        </div>
        {errors.provider && (
          <ErrorMessage message={errors.provider.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons onPrev={handlePrev} />
    </form>
  );
};
